import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import Item from "../../components/Item";
import CustomButton from "../../components/CustomButton";
import SpeedRankService from "./SpeedRankService";
import Logger from "../../../services/Logger";
import { speedRankPuzzleDetailPath } from "./speedRankPaths";

const PUZZLE_TYPE_OPTIONS = [
    { value: "JIGSAW", label: "Jigsaw" },
    { value: "SLIDING", label: "Sliding" },
];

const TEST_FILTER_OPTIONS = [
    { value: "exclude", label: "Exclude testers" },
    { value: "all", label: "List all" },
];

function formatSeconds(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return "—";
    const m = Math.floor(n / 60);
    const s = Math.floor(n % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

function PuzzleTitleLink({ puzzleType, storyId, title }) {
    if (!title) {
        return null;
    }
    const href = speedRankPuzzleDetailPath(puzzleType, storyId);
    if (!href) {
        return title;
    }
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
        >
            {title}
        </Link>
    );
}

/**
 * Puzzle tab: each mode group (goal) with top speeds.
 */
export default function SpeedRankPuzzlePanel({
    puzzleType: initialPuzzleType = "JIGSAW",
    testFilter: initialTestFilter = "exclude",
}) {
    const [puzzleType, setPuzzleType] = useState(
        initialPuzzleType === "SLIDING" ? "SLIDING" : "JIGSAW"
    );
    const [testFilter, setTestFilter] = useState(
        initialTestFilter === "all" ? "all" : "exclude"
    );
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await SpeedRankService.listModes({
                puzzleType,
                testFilter,
                limit: 5,
            });
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to load mode groups");
                setModes([]);
                Logger.warn("speed-rank modes load failed:", result);
                return;
            }
            const list = Array.isArray(result) ? result : (Array.isArray(result?.content) ? result.content : []);
            setModes(list);
        } catch (err) {
            setModes([]);
            setError(err?.message || "Failed to load mode groups");
            Logger.error("speed-rank modes load:", err);
        } finally {
            setLoading(false);
        }
    }, [puzzleType, testFilter]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Box>
            <Item sx={{ mb: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "center",
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="sr-puzzle-tab-type-label">Puzzle type</InputLabel>
                        <Select
                            labelId="sr-puzzle-tab-type-label"
                            label="Puzzle type"
                            value={puzzleType}
                            onChange={(e) => setPuzzleType(e.target.value)}
                        >
                            {PUZZLE_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sr-puzzle-tab-test-label">Testers</InputLabel>
                        <Select
                            labelId="sr-puzzle-tab-test-label"
                            label="Testers"
                            value={testFilter}
                            onChange={(e) => setTestFilter(e.target.value)}
                        >
                            {TEST_FILTER_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <CustomButton variant="outlined" onClick={() => load()} disabled={loading}>
                        Refresh
                    </CustomButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Each row is a mode goal. Top speed is the fastest personal best in that group.
                </Typography>
                {error ? (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                ) : null}
            </Item>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                    },
                    gap: 2,
                }}
            >
                {loading && modes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading…
                    </Typography>
                ) : null}
                {modes.map((mode) => {
                    const top = Array.isArray(mode.top) ? mode.top : [];
                    const hasBest = mode.bestSeconds != null;
                    return (
                        <Item key={mode.modeKey || mode.modeLabel} sx={{ minHeight: 160 }}>
                            <Stack spacing={1.25}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {mode.modeLabel || mode.modeKey}
                                    </Typography>
                                    <Chip size="small" label="Goal" variant="outlined" />
                                </Box>
                                {hasBest ? (
                                    <>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    opacity: 0.7,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Top speed
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                                                {formatSeconds(mode.bestSeconds)}
                                            </Typography>
                                            <Typography variant="body2" component="div">
                                                {mode.nickname || "Player"}
                                                {mode.puzzleTitle ? (
                                                    <>
                                                        {" · "}
                                                        <PuzzleTitleLink
                                                            puzzleType={mode.puzzleType || puzzleType}
                                                            storyId={mode.storyId}
                                                            title={mode.puzzleTitle}
                                                        />
                                                    </>
                                                ) : null}
                                            </Typography>
                                        </Box>
                                        {top.length > 1 ? (
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ display: "block", opacity: 0.7, mb: 0.5 }}
                                                >
                                                    Top {top.length}
                                                </Typography>
                                                {top.map((entry) => (
                                                    <Typography
                                                        key={`${entry.rank}-${entry.nickname}-${entry.seconds}`}
                                                        variant="body2"
                                                        component="div"
                                                        sx={{ lineHeight: 1.55 }}
                                                    >
                                                        #{entry.rank} {entry.nickname || "Player"} ·{" "}
                                                        {formatSeconds(entry.seconds)}
                                                        {entry.puzzleTitle ? (
                                                            <>
                                                                {" · "}
                                                                <PuzzleTitleLink
                                                                    puzzleType={mode.puzzleType || puzzleType}
                                                                    storyId={entry.storyId}
                                                                    title={entry.puzzleTitle}
                                                                />
                                                            </>
                                                        ) : null}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ) : null}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No times yet for this mode.
                                    </Typography>
                                )}
                            </Stack>
                        </Item>
                    );
                })}
            </Box>
        </Box>
    );
}
